/**
 * Socket 模块入口文件
 * 提供 Socket 服务的创建和管理功能
 */

import { SocketService } from './service'
import { DEFAULT_URL } from './config'
import { SocketEventManager } from './event'

import type { ConnectionState, EventHandler } from './types'

declare global {
  interface Window {
    __SOCKET_INSTANCE_MAP__?: Map<string, SocketInstance>
    $socket?: SocketInstance
  }
}

export type SocketArg
  = | { type: 'url', value: string }

// Socket通信方法接口
export interface SocketEvents {
  on: <T = any>(event: string, handler: (data: T) => void) => void
  once: <T = any>(event: string, handler: (data: T) => void) => void
  off: (event: string, handler?: EventHandler) => void
  emit: (event: string, data: any) => Promise<void>
  waitFor: <T = any>(event: string) => Promise<T>
}

// Socket管理方法接口
export interface SocketManager {
  connect: () => void
  disconnect: () => void
  reconnect: () => void
  getState: () => ConnectionState
  isConnected: () => boolean
  isAuthenticated: () => boolean
  destroy: () => void
}

// Socket实例接口
export interface SocketInstance {
  socket: SocketService
  on: (event: string, handler: any) => void
  once: (event: string, handler: any) => void
  off: (event: string, handler?: any) => void
  emit: (event: string, data: any) => Promise<void>
  destroySocket: () => void
  getAllSockets: () => Map<string, SocketInstance>
  destroyAllSockets: () => void
  isConnected: () => boolean
  isAuthenticated: () => boolean
  On: (event: string, options?: SocketEventOptions) => MethodDecorator
  Once: (event: string, options?: SocketEventOptions) => MethodDecorator
  reconnect: () => void
}

/**
 * 销毁所有Socket实例
 */
function destroyAllSockets(): void {
  window.__SOCKET_INSTANCE_MAP__ = undefined
}

function getAllSockets(): Map<string, SocketInstance> {
  if (!globalSocketInstance)
    return new Map()
  return new Map([[DEFAULT_WS_URL, globalSocketInstance]])
}

// 1. 使用全局单例模式
let globalSocketInstance: SocketInstance | null = null;

export function getGlobalSocket(): SocketInstance {
  if (!globalSocketInstance) {
    globalSocketInstance = useSocket();
  }
  return globalSocketInstance;
}

function getOrCreateInstance(url: string, roomId?: string | number, token?: string, userId?: string): SocketInstance {
  const normalizedRoomId = roomId ?? -1
  const key = `${url}-${normalizedRoomId}`

  // 初始化全局 Map
  if (!window.__SOCKET_INSTANCE_MAP__) {
    window.__SOCKET_INSTANCE_MAP__ = new Map()
  }
  // 已有实例直接返回
  if (window.__SOCKET_INSTANCE_MAP__.has(key)) {
    return window.__SOCKET_INSTANCE_MAP__.get(key)!
  }

  // 创建新实例
  const socket = SocketService.getInstance(url)
  
  // 如果提供了用户ID，设置到socket实例中
  if (userId) {
    socket.setUserId(userId)
  }
  
  const event = new SocketEventManager(socket.getSocket())

  const instance: SocketInstance = {
    socket,
    on: (eventName: string, handler: any) => event.on(eventName, handler),
    once: (eventName: string, handler: any) => event.once(eventName, handler),
    off: (eventName: string, handler?: any) => event.off(eventName, handler),
    emit: (eventName: string, data: any) => event.emit(eventName, data),
    destroySocket: () => {
      // 只断开连接，不删除实例
      socket.disconnect()
    },
    getAllSockets: () => window.__SOCKET_INSTANCE_MAP__!,
    destroyAllSockets: () => {
      socket.disconnect()
      window.__SOCKET_INSTANCE_MAP__!.clear()
    },
    isConnected: () => socket.isConnected(),
    isAuthenticated: () => socket.isAuthenticated(),
    On,
    Once,
    reconnect: () => socket.reconnect(),
  }

  window.__SOCKET_INSTANCE_MAP__.set(key, instance)
  return instance
}

// 1. 先定义一个全局的默认 URL，使用动态域名
const DEFAULT_WS_URL = DEFAULT_URL

/**
 * Socket事件装饰器配置选项
 */
export interface SocketEventOptions {
  /** 是否自动取消监听 */
  autoOff?: boolean
  /** 连接状态检查 */
  requireConnected?: boolean
  /** 认证状态检查 */
  requireAuthenticated?: boolean
}

/**
 * Socket事件监听装饰器
 * @example
 * // 基本使用
 * @On('message')
 * handleMessage(data) {
 *   console.log(data);
 * }
 *
 * // 带配置的使用
 * @On('message', { requireConnected: true })
 * handleMessageWhenConnected(data) {
 *   console.log(data);
 * }
 */
function On(event: string, options: SocketEventOptions = {}) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value
    const socket = useSocket()

    const {
      autoOff = false,
      requireConnected = false,
      requireAuthenticated = false,
    } = options

    // 创建事件处理函数
    const handler = (data: any) => {
      // 检查连接状态
      if (requireConnected && !socket.isConnected()) {
        console.warn(`[Socket] Event ${event} requires connected state`)
        return
      }

      // 检查认证状态
      if (requireAuthenticated && !socket.isAuthenticated()) {
        console.warn(`[Socket] Event ${event} requires authenticated state`)
        return
      }

      // 调用原始方法
      originalMethod.call(target, data)

      // 如果设置了自动取消监听，则在处理完事件后移除监听器
      if (autoOff) {
        socket.off(event, handler)
      }
    }

    // 注册事件监听
    socket.on(event, handler)

    // 保存清理函数
    const cleanup = () => {
      socket.off(event, handler)
    }

    // 如果组件有onUnmounted生命周期，自动清理
    if (target.onUnmounted) {
      const originalUnmounted = target.onUnmounted
      target.onUnmounted = () => {
        cleanup()
        originalUnmounted.call(target)
      }
    }

    return descriptor
  }
}

/**
 * Socket一次性事件监听装饰器
 * @example
 * // 基本使用
 * @Once('message')
 * handleMessageOnce(data) {
 *   console.log(data);
 * }
 *
 * // 带配置的使用
 * @Once('message', { requireAuthenticated: true })
 * handleMessageOnceWhenAuthenticated(data) {
 *   console.log(data);
 * }
 */
function Once(event: string, options: SocketEventOptions = {}) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value
    const socket = useSocket()

    const {
      requireConnected = false,
      requireAuthenticated = false,
    } = options

    // 创建事件处理函数
    const handler = (data: any) => {
      // 检查连接状态
      if (requireConnected && !socket.isConnected()) {
        console.warn(`[Socket] Event ${event} requires connected state`)
        return
      }

      // 检查认证状态
      if (requireAuthenticated && !socket.isAuthenticated()) {
        console.warn(`[Socket] Event ${event} requires authenticated state`)
        return
      }

      // 调用原始方法
      originalMethod.call(target, data)
    }

    // 注册一次性事件监听
    socket.once(event, handler)

    return descriptor
  }
}

// 添加 Vue 组件装饰器支持
declare module 'vue' {
  interface ComponentCustomOptions {
    socket?: SocketInstance
  }
}

/**
 * 创建或获取 Socket 服务实例
 * @example
 * const socket = useSocket('wss://example.com', 'room1');
 *
 * // 监听事件
 * socket.on('message', (data) => {
 *   console.log(data);
 * });
 *
 * // 发送事件
 * socket.emit('send', { text: 'hello' });
 *
 * // 销毁连接
 * socket.destroySocket();
 */
export function useSocket(url?: string, userId?: string): SocketInstance {
  // 使用固定的默认 URL
  const wsUrl: string = url || DEFAULT_WS_URL
  // if (wsUrl.startsWith('ws://') || wsUrl.startsWith('wss://') || wsUrl.startsWith('http://') || wsUrl.startsWith('https://')) {
  return getOrCreateInstance(wsUrl, undefined, undefined, userId)
  // } else {
  //   throw new Error('Invalid WebSocket URL')
  // }
}

/**
 * 销毁指定Socket实例
 */
export function destroySocket(url: string = DEFAULT_URL, roomId?: string): void {
  const key = `${url}-${roomId || ''}`
  if (globalSocketInstance) {
    globalSocketInstance.destroySocket()
  }
}

// 导出 Socket 服务类
export { SocketService } from './service'
// 导出事件类型
export { SocketEvent } from './config'
// 导出类型定义
export type { ConnectionState, SocketError } from './types'
